import { useEffect, useRef, useState } from 'react';

let animatingTimeout: NodeJS.Timeout | undefined;
let heightChangeTimout: NodeJS.Timeout;
let cooldownTimeout: NodeJS.Timeout;

const COOLDOWN_PERIOD = 2000;
const TIME_IN_ALTERNATION = 500;

interface UseScoreValueAlternatorProps {
  valueChangeTrigger: any;
  lowValue: number;
  highValue: number;
}

export const useScoreValueAlternator = ({
  valueChangeTrigger,
  lowValue,
  highValue,
}: UseScoreValueAlternatorProps) => {
  const [scoreValue, setScoreValue] = useState(lowValue);
  const [shouldStartAnimating, setShouldStartAnimating] = useState(false);
  // ref that we can use in the useEffect that triggers the animation BUT it can't be in the dependency array
  // because we don't want to retrigger the effect when isAnimating changes
  const isAnimatingRef = useRef(shouldStartAnimating);

  const isInitialRender = useRef(true);

  useEffect(() => {
    isAnimatingRef.current = shouldStartAnimating;
  }, [shouldStartAnimating]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (!isAnimatingRef.current) {
      setShouldStartAnimating(true);

      animatingTimeout = setTimeout(() => {
        setShouldStartAnimating(false);
      }, COOLDOWN_PERIOD);
    }
    return () => clearTimeout(animatingTimeout);
  }, [valueChangeTrigger]);

  useEffect(() => {
    if (shouldStartAnimating) {
      setScoreValue(highValue);
      heightChangeTimout = setTimeout(() => {
        setScoreValue(lowValue);
        cooldownTimeout = setTimeout(() => {
          setShouldStartAnimating(false);
        }, COOLDOWN_PERIOD);
      }, TIME_IN_ALTERNATION);
    } else {
      setScoreValue(lowValue);
    }
    return () => {
      clearTimeout(heightChangeTimout);
      clearTimeout(cooldownTimeout);
    };
  }, [shouldStartAnimating, lowValue, highValue]);

  return scoreValue;
};
